import { useState, useEffect } from 'react'
import { Route, Switch, useParams } from 'react-router-dom'

import { Box } from '@redriverpress/system'

import { MoveItemModal } from 'Packages/folders'
import AddToClassDialog from 'Common/components/dialogs/AddToClassDialog'
import SubscribeNowDialog from 'Common/components/dialogs/SubscribeNowDialog'
import useUserAccess from 'Common/hooks/useUserAccess'
import {
  CONTENT_TYPES,
  COURSE_TYPE_LABELS,
} from 'Common/constants/contentTypes'
import AdaptiveTabs from 'Common/components/AdaptiveTabs'
import analyticsTrack from 'Common/utils/analytics'

import getInitialState from 'Common/utils/getInitialState'
import getLevelsByType from 'Common/utils/getLevelsByType'
import { useGetCourse } from '../hooks/useGetCourses'
import CourseHeader from './components/CourseHeader'
import CourseOverview from './components/CourseOverview'
import CourseFocus from './components/CourseFocus'
import CourseMaterials from './components/CourseMaterials'
import CourseNotes from './components/CourseNotes'

export default function Course() {
  const { slug } = useParams()
  const { isLoading, courseData } = useGetCourse({ slug })
  const [isSubscribeDialogOpen, setIsSubscribeDialogOpen] = useState(false)
  const [moveItemModalOpen, setMoveItemModalOpen] = useState(false)
  const { userLoggedIn, userHasAccess } = useUserAccess()

  const [dialogOpen, setDialogOpen] = useState(false)

  const { levelPreference, elliiPlusAccess } = getInitialState()

  const handleAddToClass = () => {
    userLoggedIn && userHasAccess
      ? setDialogOpen(true)
      : setIsSubscribeDialogOpen(true)
  }

  const tabs = [
    { to: `/${courseData?.slug}/materials`, label: 'Materials' },
    { to: `/${courseData?.slug}/overview`, label: 'Overview' },
  ]
  if (courseData?.notes)
    tabs.push({
      to: `/${courseData.slug}/notes`,
      label: 'Teachers’ Notes',
    })

  useEffect(() => {
    !isLoading &&
      analyticsTrack('Opened Course', {
        slug: courseData.slug,
        courseType: courseData.courseType,
      })
  }, [])

  return (
    !isLoading && (
      <Box bg='gray50'>
        <CourseHeader
          audiences={courseData.audiences}
          bannerBackground={courseData.hexColor}
          headerImageUrl={courseData.banner}
          levels={getLevelsByType(levelPreference, courseData)}
          onAddCourseToClass={handleAddToClass}
          onSaveCourseToFolder={() => setMoveItemModalOpen(true)}
          subtitle={courseData.subtitle}
          title={courseData.title}
          courseType={courseData.courseType}
          description={courseData.description}
          displaySelfStudyLabel={
            elliiPlusAccess && courseData.elliiPlusAvailable
          }
          selfStudyLevelsList={courseData.selfStudyLevelsList}
          userLoggedIn={userLoggedIn}
        />

        <Box mt={[0, null, null, 4]} mb={4}>
          <AdaptiveTabs tabs={tabs} />
        </Box>

        <Switch>
          <Route path={`/${courseData.slug}/materials`}>
            <CourseMaterials
              onAddCourseToClass={handleAddToClass}
              contents={courseData.contents}
            />
          </Route>
          <Route path={`/${courseData.slug}/overview`}>
            {courseData.focus && <CourseFocus words={courseData.focus} />}
            {courseData.overview && (
              <CourseOverview content={courseData.overview} />
            )}
          </Route>
          <Route path={`/${courseData.slug}/notes`}>
            {courseData.notes && <CourseNotes content={courseData.notes} />}
          </Route>
        </Switch>

        <AddToClassDialog
          open={dialogOpen}
          onOpenChange={() => setDialogOpen(false)}
          onCancel={() => setDialogOpen(false)}
          contentType={CONTENT_TYPES.course}
          courseType={courseData.courseType}
          content={{ id: courseData.id, title: courseData.title }}
        />

        <SubscribeNowDialog
          subtitle={`To add this ${
            COURSE_TYPE_LABELS[courseData.courseType]
          } to a class, please sign in or purchase a subscription.`}
          open={isSubscribeDialogOpen}
          onOpenChange={isOpen => setIsSubscribeDialogOpen(isOpen)}
        />

        {!isLoading && !!courseData && (
          <MoveItemModal
            isOpen={moveItemModalOpen}
            item={{
              fileable: {
                id: courseData?.id,
                type: CONTENT_TYPES.course,
              },
            }}
            onClose={reload => {
              setMoveItemModalOpen(false)
              reload && window.location.reload()
            }}
          />
        )}
      </Box>
    )
  )
}
